// Admin API Services for Party, PartyAccount, Token, etc.
import { apiCall, ApiResponse } from './api';

// Party Models
export interface Party {
  id?: number;
  partyName: string;
  mobileNo: string;
  emailId: string;
  address: string;
  panNo: string;
  notes: string;
  isActive: boolean;
}

export interface PartySearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// Party Account Models
export interface PartyAccount {
  id?: number;
  partyId: number;
  accountTypeId: number;
  bankName: string;
  holderName: string;
  accountNo: string;
  ifscCode: string;
  branchName: string;
  notes: string;
  upI_ID:string;
  linkBankName:string;
  qrLabel:string;
  qrTypeId:number;
  qrImageUrl:string;
  walletAddress:string;
  walletLabel:string;
  networkId:number;
  isActive: boolean;
}

export interface PartyAccountList extends PartyAccount {
  partyName?: string;
  accountType?: string;
}

export interface PartyAccountSearchRequest {
  partyId?: number;
  accountTypeId?: number;
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// Token Models
export interface Token {
  id?: number;
  tokenName: string;
  symbol: string;
  tokenAddress: string;
  openingBal: string;
  openingDate: string;
  currentBal: string;
  notes: string;
  isActive: boolean;
}

export interface TokenSearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// Internal Account Models
export interface InternalAccount {
  id?: number;
  accountName: string;
  accountTypeId: number;
  accountType?: string;
  bankName: string;
  holderName: string;
  accountNo: string;
  ifscCode: string;
  branchName: string;
  openingBal: number;
  openingDate: string;
  currentBal: number;
  isActive: boolean;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface InternalAccountSearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// Payment Mode Models
export interface PaymentMode {
  id?: number;
  paymentModeName: string;
  paymentTypeId: number;
  paymentType?: string;
  isActive: boolean;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface PaymentModeSearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// User Models
export interface User {
  id?: number;
  personName: string;
  userName: string;
  userType: string;
  isActive: boolean;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface UserSearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// Audit Log Models
export interface AuditLog {
  id?: number;
  timestamp?: string;
  userName?: string;
  action?: string;
  entityType?: string;
  remarks?: string;
  // Backend fields (for compatibility)
  createdDate?: string;
  creatorName?: string;
  actionType?: string;
}

export interface AuditLogSearchRequest {
  entityType?: string;
  actionType?: string;
  pageNo?: number;
  pageSize?: number;
  limit?: number;
  searchText?: string;
}

// Dollar Account Models
export interface DollarAccount {
  id?: number;
  accountName: string;
  accountTypeId: number;
  accountType?: string;
  bankName: string;
  holderName: string;
  accountNo: string;
  ifsC_SwiftCode: string;
  openingBal: number;
  openingDate: string;
  currentBal: number;
  isActive: boolean;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface DollarAccountSearchRequest {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// ============ Party APIs ============

export const savePartyAPI = async (party: Party): Promise<ApiResponse> => {
  debugger
  return apiCall('/adminmaster/SaveParty', 'POST', party, true);
};

export const getPartyListAPI = async (
  searchRequest: PartySearchRequest
): Promise<ApiResponse<Party[]>> => {
  return apiCall<Party[]>('/adminmaster/GetPartyList', 'POST', searchRequest, true);
};

export const getPartyByIdAPI = async (id: number): Promise<ApiResponse<Party>> => {
  return apiCall<Party>('/adminmaster/GetPartyById', 'POST', { id }, true);
};

export const deletePartyAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeleteParty?Id=${id}`, 'POST', undefined, true);
};

// ============ Party Account APIs ============

export const savePartyAccountAPI = async (
  account: PartyAccount
): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SavePartyAccount', 'POST', account, true);
};

export const getPartyAccountListAPI = async (
  searchRequest: PartyAccountSearchRequest
): Promise<ApiResponse<PartyAccountList[]>> => {
  return apiCall<PartyAccountList[]>(
    '/adminmaster/GetPartyAccountList',
    'POST',
    searchRequest,
    true
  );
};

export const getPartyAccountByIdAPI = async (
  id: number
): Promise<ApiResponse<PartyAccount>> => {
  return apiCall<PartyAccount>('/adminmaster/GetPartyAccountById', 'POST', { id }, true);
};

export const deletePartyAccountAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeletePartyAccount?Id=${id}`, 'POST', undefined, true);
};

// ============ Token APIs ============

export const saveTokenAPI = async (token: Token): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SaveToken', 'POST', token, true);
};

export const getTokenListAPI = async (
  searchRequest: TokenSearchRequest
): Promise<ApiResponse<Token[]>> => {
  return apiCall<Token[]>('/adminmaster/GetTokenList', 'POST', searchRequest, true);
};

export const getTokenByIdAPI = async (id: number): Promise<ApiResponse<Token>> => {
  return apiCall<Token>('/adminmaster/GetTokenById', 'POST', { id }, true);
};

export const deleteTokenAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeleteToken?Id=${id}`, 'POST', undefined, true);
};

// ============ Internal Account APIs ============

export const saveInternalAccountAPI = async (
  account: InternalAccount
): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SaveInternalAccount', 'POST', account, true);
};

export const getInternalAccountListAPI = async (
  searchRequest: InternalAccountSearchRequest
): Promise<ApiResponse<InternalAccount[]>> => {
  return apiCall<InternalAccount[]>(
    '/adminmaster/GetInternalAccountList',
    'POST',
    searchRequest,
    true
  );
};

export const getInternalAccountByIdAPI = async (
  id: number
): Promise<ApiResponse<InternalAccount>> => {
  return apiCall<InternalAccount>(
    '/adminmaster/GetInternalAccountById',
    'POST',
    { id },
    true
  );
};

export const deleteInternalAccountAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeleteInternalAccount?Id=${id}`, 'POST', undefined, true);
};

// ============ Payment Mode APIs ============

export const savePaymentModeAPI = async (
  mode: PaymentMode
): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SavePaymentMode', 'POST', mode, true);
};

export const getPaymentModeListAPI = async (
  searchRequest: PaymentModeSearchRequest
): Promise<ApiResponse<PaymentMode[]>> => {
  return apiCall<PaymentMode[]>(
    '/adminmaster/GetPaymentModeList',
    'POST',
    searchRequest,
    true
  );
};

export const getPaymentModeByIdAPI = async (
  id: number
): Promise<ApiResponse<PaymentMode>> => {
  return apiCall<PaymentMode>('/adminmaster/GetPaymentModeById', 'POST', { id }, true);
};

export const deletePaymentModeAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeletePaymentMode?Id=${id}`, 'POST', undefined, true);
};

// ============ Dollar Account APIs ============

export const saveDollarAccountAPI = async (
  account: DollarAccount
): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SaveDollarAccount', 'POST', account, true);
};

export const getDollarAccountListAPI = async (
  searchRequest: DollarAccountSearchRequest
): Promise<ApiResponse<DollarAccount[]>> => {
  return apiCall<DollarAccount[]>(
    '/adminmaster/GetDollarAccountList',
    'POST',
    searchRequest,
    true
  );
};

export const getDollarAccountByIdAPI = async (
  id: number
): Promise<ApiResponse<DollarAccount>> => {
  return apiCall<DollarAccount>(
    '/adminmaster/GetDollarAccountById',
    'POST',
    { id },
    true
  );
};

export const deleteDollarAccountAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/adminmaster/DeleteDollarAccount?Id=${id}`, 'POST', undefined, true);
};


// ============ User APIs ============

export interface Module_Response {
  id?: number;
  moduleName?: string;
  isActive?: boolean;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface Module_Search {
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

export interface UserModulePermission_Response {
  // From BaseEntity
  id?: number;
  // From BaseResponseEntity
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
  // From UserModulePermission_Response
  employeeId?: number | string;
  userName?: string;
  moduleId?: number;
  moduleName?: string;
  isView?: boolean;
  isAdd?: boolean;
  isEdit?: boolean;
  isVoid?: boolean;
  isExport?: boolean;
  isActive?: boolean;
}

export interface UserModulePermission_Request {
  id?: number;
  employeeId?: number;
  moduleId?: number;
  isView?: boolean;
  isAdd?: boolean;
  isEdit?: boolean;
  isVoid?: boolean;
  isExport?: boolean;
  isActive?: boolean;
}

export interface UserModulePermission_Search {
  employeeId?: number;
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

export const saveUserAPI = async (user: User & { uPassword?: string }): Promise<ApiResponse> => {
  return apiCall('/user/SaveUser', 'POST', user, true);
};

export const getUserListAPI = async (
  searchRequest: UserSearchRequest
): Promise<ApiResponse<User[]>> => {
  return apiCall<User[]>('/user/GetUserList', 'POST', searchRequest, true);
};

export const getUserByIdAPI = async (id: number): Promise<ApiResponse<User>> => {
  return apiCall<User>('/user/GetUserById', 'POST', { id }, true);
};

export const deleteUserAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/user/DeleteUser?Id=${id}`, 'POST', undefined, true);
};

export const getModuleListAPI = async (searchRequest?: Module_Search): Promise<ApiResponse<Module_Response[]>> => {
  return apiCall<Module_Response[]>('/adminmaster/GetModuleList', 'POST', searchRequest || {}, true);
};

export const saveUserModulePermissionAPI = async (permission: UserModulePermission_Request): Promise<ApiResponse> => {
  return apiCall('/adminmaster/SaveUserModulePermission', 'POST', permission, true);
};

export const getUserModulePermissionListAPI = async (searchRequest: UserModulePermission_Search): Promise<ApiResponse<UserModulePermission_Response[]>> => {
  return apiCall<UserModulePermission_Response[]>('/adminmaster/GetUserModulePermissionList', 'POST', searchRequest, true);
};

// ============ Audit Log APIs ============

export const getAuditLogListAPI = async (
  searchRequest: AuditLogSearchRequest
): Promise<ApiResponse<AuditLog[]>> => {
  return apiCall<AuditLog[]>(
    '/user/GetAuditLogList',
    'POST',
    searchRequest,
    true
  );
};

// ============ Change Password API ============

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const changePasswordAPI = async (
  request: ChangePasswordRequest
): Promise<ApiResponse> => {
  return apiCall('/user/ChangePassword', 'POST', request, true);
};

// ============ Buy Transaction Models ============

export interface BuyRequest {
  id?: number;
  partyId?: number;
  buyDate?: string;
  paymentInId?: number;
  tokenId?: number;
  buyQty?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  totalAmtInINR?: number;
  paymentModeId?: number;
  fromAccountId?: number;
  payToAccountId?: number;
  referenceNo?: string;
  paymentDate?: string;
  remarks?: string;
}

export interface BuyResponse {
  id?: number;
  partyId?: number;
  partyName?: string;
  buyDate?: string;
  paymentInId?: number;
  paymentIn?: string;
  tokenId?: number;
  tokenName?: string;
  buyQty?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  totalAmtInINR?: number;
  paymentModeId?: number;
  paymentModeName?: string;
  fromAccountId?: number;
  fromAccountName?: string;
  payToAccountId?: number;
  payToAccountName?: string;
  referenceNo?: string;
  paymentDate?: string;
  remarks?: string;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface BuySearch {
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  tokenId?: number;
  paymentInId?: number;
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// ============ Sell Transaction Models ============

export interface SellRequest {
  id?: number;
  partyId?: number;
  sellDate?: string;
  paymentInId?: number;
  tokenId?: number;
  sellQty?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  totalAmtInINR?: number;
  paymentModeId?: number;
  fromAccountId?: number;
  payToAccountId?: number;
  referenceNo?: string;
  paymentDate?: string;
  remarks?: string;
}

export interface SellResponse {
  id?: number;
  partyId?: number;
  partyName?: string;
  sellDate?: string;
  paymentInId?: number;
  paymentIn?: string;
  tokenId?: number;
  tokenName?: string;
  sellQty?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  totalAmtInINR?: number;
  paymentModeId?: number;
  paymentModeName?: string;
  fromAccountId?: number;
  fromAccountName?: string;
  payToAccountId?: number;
  payToAccountName?: string;
  referenceNo?: string;
  paymentDate?: string;
  remarks?: string;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface SellSearch {
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  tokenId?: number;
  paymentInId?: number;
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// ============ Swap Transaction Models ============

export interface SwapRequest {
  id?: number;
  swapTypeId?: number;
  swapDate?: string;
  paymentInId?: number;
  partyId?: number;
  fromAccountId?: number;
  toAccountId?: number;
  quantity?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  slippagePerct?: number;
  baseAmount?: number;
  slippageAmount?: number;
  finalAmount?: number;
  remarks?: string;
}

export interface SwapResponse {
  id?: number;
  swapTypeId?: number;
  swapType?: string;
  swapDate?: string;
  paymentInId?: number;
  paymentIn?: string;
  partyId?: number;
  partyName?: string;
  fromAccountId?: number;
  fromAccountName?: string;
  toAccountId?: number;
  toAccountName?: string;
  quantity?: number;
  tokenToDollarRate?: number;
  dollarToINRRate?: number;
  slippagePerct?: number;
  baseAmount?: number;
  slippageAmount?: number;
  finalAmount?: number;
  remarks?: string;
  creatorName?: string;
  createdBy?: number;
  createdDate?: string;
  modifierName?: string;
  modifiedBy?: number;
  modifiedDate?: string;
}

export interface SwapSearch {
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  swapTypeId?: number;
  paymentInId?: number;
  searchText?: string;
  pageNo?: number;
  pageSize?: number;
  total?: number;
}

// ============ Buy Transaction APIs ============

export const saveBuyAPI = async (
  request: BuyRequest
): Promise<ApiResponse> => {
  return apiCall('/transaction/SaveBuy', 'POST', request, true);
};

export const getBuyListAPI = async (
  searchRequest: BuySearch
): Promise<ApiResponse<BuyResponse[]>> => {
  return apiCall<BuyResponse[]>(
    '/transaction/GetBuyList',
    'POST',
    searchRequest,
    true
  );
};

export const getBuyByIdAPI = async (
  id: number
): Promise<ApiResponse<BuyResponse>> => {
  return apiCall<BuyResponse>(
    '/transaction/GetBuyById',
    'POST',
    { id },
    true
  );
};

// ============ Sell Transaction APIs ============

export const saveSellAPI = async (
  request: SellRequest
): Promise<ApiResponse> => {
  return apiCall('/transaction/SaveSell', 'POST', request, true);
};

export const getSellListAPI = async (
  searchRequest: SellSearch
): Promise<ApiResponse<SellResponse[]>> => {
  return apiCall<SellResponse[]>(
    '/transaction/GetSellList',
    'POST',
    searchRequest,
    true
  );
};

export const getSellByIdAPI = async (
  id: number
): Promise<ApiResponse<SellResponse>> => {
  return apiCall<SellResponse>(
    '/transaction/GetSellById',
    'POST',
    { id },
    true
  );
};

// ============ Swap Transaction APIs ============

export const saveSwapAPI = async (
  request: SwapRequest
): Promise<ApiResponse> => {
  return apiCall('/transaction/SaveSwap', 'POST', request, true);
};

export const getSwapListAPI = async (
  searchRequest: SwapSearch
): Promise<ApiResponse<SwapResponse[]>> => {
  return apiCall<SwapResponse[]>(
    '/transaction/GetSwapList',
    'POST',
    searchRequest,
    true
  );
};

export const getSwapByIdAPI = async (
  id: number
): Promise<ApiResponse<SwapResponse>> => {
  return apiCall<SwapResponse>(
    '/transaction/GetSwapById',
    'POST',
    { id },
    true
  );
};

// ============ Delete Transaction APIs ============

export const deleteBuyAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/transaction/DeleteBuy?Id=${id}`, 'POST', undefined, true);
};

export const deleteSellAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/transaction/DeleteSell?Id=${id}`, 'POST', undefined, true);
};

export const deleteSwapAPI = async (id: number): Promise<ApiResponse> => {
  return apiCall(`/transaction/DeleteSwap?Id=${id}`, 'POST', undefined, true);
};

// ============ Daily Closing APIs ============

export interface DailyClosingReportRequest {
  fromDate?: string;
  toDate?: string;
}

export interface DailyClosingReportResponse {
  id?: number;
  totalTransaction?: number;
  totalInAmt?: number;
  totalOutAmt?: number;
  totalNetAmt?: number;
}

export interface TokenMovementRequest {
  fromDate?: string;
  toDate?: string;
  searchText: string;
  pageNo?: number;
  pageSize?: number;
}

export interface TokenMovementResponse {
  id: number;
  tokenName: string;
  symbol: string;
  inAmt: number;
  outAmt: number;
}

export interface INRMovementRequest {
  fromDate?: string;
  toDate?: string;
  searchText: string;
  pageNo?: number;
  pageSize?: number;
}

export interface INRMovementResponse {
  id: number;
  accountName: string;
  accountNo: string;
  inAmt: number;
  outAmt: number;
}

export interface DollarMovementRequest {
  fromDate?: string;
  toDate?: string;
  searchText: string;
  pageNo?: number;
  pageSize?: number;
}

export interface DollarMovementResponse {
  id: number;
  accountName: string;
  accountNo: string;
  inAmt: number;
  outAmt: number;
}

export const getDailyClosingReportAPI = async (
  request: DailyClosingReportRequest
): Promise<ApiResponse<DailyClosingReportResponse>> => {
  return apiCall<DailyClosingReportResponse>(
    '/ManageReport/GetDailyClosingReport',
    'POST',
    request,
    true
  );
};

export const getTokenMovementAPI = async (
  request: TokenMovementRequest
): Promise<ApiResponse<TokenMovementResponse[]>> => {
  return apiCall<TokenMovementResponse[]>(
    '/ManageReport/GetTokenMovementReport',
    'POST',
    request,
    true
  );
};

export const getINRMovementAPI = async (
  request: INRMovementRequest
): Promise<ApiResponse<INRMovementResponse>> => {
  return apiCall<INRMovementResponse>(
    '/ManageReport/GetINRMovementReport',
    'POST',
    request,
    true
  );
};

export const getDollarMovementAPI = async (
  request: DollarMovementRequest
): Promise<ApiResponse<DollarMovementResponse>> => {
  return apiCall<DollarMovementResponse>(
    '/ManageReport/GetDollarMovementReport',
    'POST',
    request,
    true
  );
};

// ============ Ledger APIs ============

export interface LedgerSearchRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  partyId?: number;
  fromDate?: string;
  toDate?: string;
  transactionTypeId?: number;
}

export interface LedgerResponse {
  id?: number;
  transactionDate?: string;
  transactionType?: string;
  partyId?: number;
  partyName?: string;
  in?: number;
  out?: number;
  balance?: number;
  remarks?: string;
}

export const getLedgerListAPI = async (
  request: LedgerSearchRequest
): Promise<ApiResponse<LedgerResponse>> => {
  return apiCall<LedgerResponse>(
    '/ManageReport/GetLedgerList',
    'POST',
    request,
    true
  );
};

// ============ Report Daily Tab APIs ============

export interface DailyReportRequest {
  startDate?: string;
  endDate?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface DailyReportResponse {
  id?: number;
  totalBuyAmt?: number;
  totalSellAmt?: number;
  totalGrossProfit?: number;
  totalTransaction?: number;
  totalBuyTransaction?: number;
  totalSellTransaction?: number;
  totalSwapTransaction?: number;
}

export interface TokenWiseReportRequest {
  startDate?: string;
  endDate?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface TokenWiseReportResponse {
  id?: number;
  tokenId?: number;
  tokenName?: string;
  symbol?: string;
  buyQty?: number;
  buyAmt?: number;
  sellQty?: number;
  sellAmt?: number;
  netQty?: number;
}

export interface INRWiseReportRequest {
  startDate?: string;
  endDate?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface INRWiseReportResponse {
  id?: number;
  accountName:string;
  accountType:string;
  inAmt?: number;
  outAmt?: number;
  netAmt?: number;
}

export interface DollarWiseReportRequest {
  startDate?: string;
  endDate?: string;
  pageNo?: number;
  pageSize?: number;
}

export interface DollarWiseReportResponse {
  id?: number;
  accountName:string;
  accountType:string;
  inAmt?: number;
  outAmt?: number;
  netAmt?: number;
}

export const getDailyReportAPI = async (
  request: DailyReportRequest
): Promise<ApiResponse<DailyReportResponse>> => {
  return apiCall<DailyReportResponse>(
    '/ManageReport/GetDailyReport',
    'POST',
    request,
    true
  );
};

export const getTokenWiseReportAPI = async (
  request: TokenWiseReportRequest
): Promise<ApiResponse<TokenWiseReportResponse[]>> => {
  return apiCall<TokenWiseReportResponse[]>(
    '/ManageReport/GetTokenWiseReport',
    'POST',
    request,
    true
  );
};

export const getINRWiseReportAPI = async (
  request: INRWiseReportRequest
): Promise<ApiResponse<INRWiseReportResponse>> => {
  return apiCall<INRWiseReportResponse>(
    '/ManageReport/GetINRWiseReport',
    'POST',
    request,
    true
  );
};

export const getDollarWiseReportAPI = async (
  request: DollarWiseReportRequest
): Promise<ApiResponse<DollarWiseReportResponse>> => {
  return apiCall<DollarWiseReportResponse>(
    '/ManageReport/GetDollarWiseReport',
    'POST',
    request,
    true
  );
};

// ============ Sale Report APIs ============

export interface SaleReportRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  tokenId?: number;
}

export interface SaleReportResponse {
  id?: number;
  sellDate?: string;
  partyId?: number;
  partyName?: string;
  sellQty?: number;
  totalAmtInINR?: number;
}

export interface SaleReportSummary {
  totalSales?: number;
  totalQtySold?: number;
  transactionCount?: number;
}

export const getSaleReportAPI = async (
  request: SaleReportRequest
): Promise<ApiResponse<SaleReportResponse[]>> => {
  return apiCall<SaleReportResponse[]>(
    '/ManageReport/GetSaleReport',
    'POST',
    request,
    true
  );
};

// ============ Purchase Report APIs ============

export interface PurchaseReportRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  tokenId?: number;
}

export interface PurchaseReportResponse {
  id?: number;
  buyDate?: string;
  partyId?: number;
  partyName?: string;
  buyQty?: number;
  totalAmtInINR?: number;
}

export interface PurchaseReportSummary {
  totalPurchase?: number;
  totalQtyBought?: number;
  transactionCount?: number;
}

export const getPurchaseReportAPI = async (
  request: PurchaseReportRequest
): Promise<ApiResponse<PurchaseReportResponse[]>> => {
  return apiCall<PurchaseReportResponse[]>(
    '/ManageReport/GetPurchaseReport',
    'POST',
    request,
    true
  );
};

// ============ Settlement Report APIs ============

export interface SettlementReportRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  fromDate?: string;
  toDate?: string;
  partyId?: number;
}

export interface SettlementReportResponse {
  id?: number;
  transactionDate?: string;
  partyId?: number;
  partyName?: string;
  transactionType?: string;
  tokenId?: number;
  tokenName?: string;
  symbol?: string;
  paymentInId?: number;
  paymentIn?: string;
  qty?: number;
  amount?: number;
  payToAccountId?: number;
  payToAccountNo?: string;
  status?: string;
}

export const getSettlementReportAPI = async (
  request: SettlementReportRequest
): Promise<ApiResponse<SettlementReportResponse[]>> => {
  return apiCall<SettlementReportResponse[]>(
    '/ManageReport/GetSettlementReport',
    'POST',
    request,
    true
  );
};

// ============ Profit Loss Report APIs ============

export interface ProfitLossReportRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  fromDate?: string;
  toDate?: string;
}

export interface ProfitLossReportResponse {
  id?: number;
  tokenName?: string;
  symbol?: string;
  buyQty?: number;
  buyAmt?: number;
  sellQty?: number;
  sellAmt?: number;
  netAmt?: number;
}

export const getProfitLossReportAPI = async (
  request: ProfitLossReportRequest
): Promise<ApiResponse<ProfitLossReportResponse[]>> => {
  return apiCall<ProfitLossReportResponse[]>(
    '/ManageReport/GetProfitLossReport',
    'POST',
    request,
    true
  );
};

// ============ Transaction History Report APIs ============

export interface TransactionHistoryReportRequest {
  pageNo?: number;
  pageSize?: number;
  searchText?: string;
  isActive?: boolean | null;
  fromDate?: string;
  toDate?: string;
  partyId?: number;
  accountId?: number;
  transactionTypeId?: number;
}

export interface TransactionHistoryReportResponse {
  id?: number;
  transactionDate?: string;
  transactionType?: string;
  partyId: string;
  partyName?: string;
  accountName?: string;
  tokenName?: string;
  symbol?: string;
  Qty?: number;
  qty?: number;
  amount?: number;
  inrBal?: number;
  tokenBal?: number;
  dollarBal?: number;
  remarks?: string;
  paymentIn1: string;
  paymentIn2: string;
}

export const getTransactionHistoryReportAPI = async (
  request: TransactionHistoryReportRequest
): Promise<ApiResponse<TransactionHistoryReportResponse[]>> => {
  return apiCall<TransactionHistoryReportResponse[]>(
    '/ManageReport/GetTransactionHistoryReport',
    'POST',
    request,
    true
  );
};

// Balance Details Request/Response Interfaces
export interface BalanceDetailRequest {
  refType?: string; // 'Token', 'INR', 'Dollar'
  refId?: number;
}

export interface BalanceDetailTransactionResponse {
  id?: number;
  transactionDate?: string;
  transactionType?: string;
  partyName?: string;
  qty?: number;
  balance?: number;
}

export interface BalanceDetailResponse {
  tokenName?: string;
  symbol?: string;
  accountName?: string;
  openingBalance?: number;
  closingBalance?: number;
  totalEntries?: number;
  transactions?: BalanceDetailTransactionResponse[];
}

export const getBalanceDetailsAPI = async (
  request: BalanceDetailRequest
): Promise<ApiResponse<BalanceDetailResponse>> => {
  return apiCall<BalanceDetailResponse>(
    '/ManageReport/GetBalanceDetails',
    'POST',
    request,
    true
  );
};
